import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'

import WebSocketStore from 'core/classes/services/WebSocketStore'
import { CrudStore } from 'core/types/crudStore'
import { NotFoundError } from 'onboarding/classes/errors/NotFoundError'
import { TOnboardingUpload } from 'onboarding/classes/models/onboardingUpload'
import {
  TPartnerExclusionFull,
  TPartnerExclusionListItem,
  TPartnerExclusionToSave,
} from 'onboarding/classes/models/partnerExclusion'
import { mapPartnerExclusionFull } from 'onboarding/services/mapping/mapPartnerExclusionFull'
import { TListParams } from 'onboarding/types/crud'
import { convertToListPayload } from 'onboarding/utils/convertToServerRequestParams'
import { DateHelper } from 'onboarding/utils/dateHelper'
import { extractFileNameFromContentDisposition } from 'onboarding/utils/headers'
import settings from 'src/config/settings'

class PartnerExclusionApi {
  public async fetchList(params: TListParams) {
    const ws = WebSocketStore.get('onboarding')
    const response = await ws.call<{ items: TPartnerExclusionListItem[] }>({
      service: 'partnerExclusion',
      action: 'fetchList',
      payload: convertToListPayload(params),
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message)
    }

    return response.data.items
  }

  public async fetchOneFull(uid: string) {
    const ws = WebSocketStore.get('onboarding')
    const response = await ws.call<{ item: TPartnerExclusionFull; files: TOnboardingUpload[] }>({
      service: 'partnerExclusion',
      action: 'fetchOneFull',
      payload: { uid },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message)
    }

    return { item: mapPartnerExclusionFull(response.data.item), files: response.data.files }
  }

  public async deleteOne(uid: string) {
    const ws = WebSocketStore.get('onboarding')
    const response = await ws.call<{ deleted: boolean }>({
      service: 'partnerExclusion',
      action: 'deleteOne',
      payload: { uid },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message)
    }

    return response.data.deleted
  }

  public async createOne({ payload, files }: ICreatePartnerExclusionParams, accessToken: string) {
    const url = `${settings.httpsServices.onboarding}/api/v0/partnerExclusion/createOne`

    const formData = new FormData()
    formData.set('payload', JSON.stringify(payload))
    files?.forEach((file, index) => {
      formData.set(`file-${index}`, file, file.name)
    })

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      body: formData,
    })
    if (!response.ok) {
      const errorMessage = (await response.text()) || 'Failed to save field'

      throw new Error(errorMessage)
    }

    const result: { item: TPartnerExclusionFull; files: TOnboardingUpload[] } =
      await response.json()

    return { item: mapPartnerExclusionFull(result.item), files: result.files }
  }

  public async downloadExclusionFiles(uid: string, archiveName: string, accessToken: string) {
    const url = `${settings.httpsServices.onboarding}/api/v0/partnerExclusion/downloadExclusionFiles`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: accessToken,
      },
      body: JSON.stringify({ uid, archiveName }),
    })
    if (response.status === 404) {
      throw new NotFoundError(await response.text())
    }

    if (!response.ok) {
      const errorMessage = (await response.text()) || 'Failed to download files'

      throw new Error(errorMessage)
    }

    const blob = await response.blob()

    return new File([blob], extractFileNameFromContentDisposition(response, `${archiveName}.zip`))
  }

  public async exportAll(
    accessToken: string,
    { filter, sort }: Pick<TListParams, 'filter' | 'sort'>
  ) {
    const url = `${settings.httpsServices.onboarding}/api/v0/partnerExclusion/export`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: accessToken,
      },
      body: JSON.stringify({ kendoConditions: filter, sort }),
    })

    if (!response.ok) {
      const errorMessage = (await response.text()) || 'Failed to export'

      throw new Error(errorMessage)
    }

    const blob = await response.blob()

    return new File(
      [blob],
      extractFileNameFromContentDisposition(
        response,
        `Partner_Exclusion_List_AMS_${DateHelper.formatToFile(DateHelper.now())}.xlsx`
      )
    )
  }

  public async updateOne(uid: string, payload: TPartnerExclusionToSave) {
    const ws = WebSocketStore.get('onboarding')
    const response = await ws.call<{ item: TPartnerExclusionFull }>({
      service: 'partnerExclusion',
      action: 'updateOne',
      payload: { uid, payload },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message)
    }

    return mapPartnerExclusionFull(response.data.item)
  }

  public getListCrud(cacheKey?: string) {
    return CrudStore.get(CrudStoreItemOnboarding.PartnerExclusionList, cacheKey)
  }
}

export const partnerExclusionApi = new PartnerExclusionApi()

export interface ICreatePartnerExclusionParams {
  payload: TPartnerExclusionToSave
  files?: File[]
}
